import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Pencil, Trash } from '@saleshandy/icons';
import {
  executeOnRequestStatus,
  getFormatNumber,
  getIsRequestPending,
  truncate,
} from '../../../../../../../shared/utils';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import { getSavedViewDefaultClassNames } from '../utils/helper';
import TruncatedTextWithTooltip from '../../../../../../../shared/components/truncated-text-with-tooltip';
import { OverlayPopover } from '../../../../../../../shared/design-system/components/overlay/popover/overlay-popover';
import {
  Placement,
  TriggerType,
} from '../../../../../../../shared/design-system/components/overlay/enums';
import { Pin } from '../../../../../../../shared/svg/pin';
import { Pined } from '../../../../../../../shared/svg/pined';
import { PinUnpinActionType } from '../../../../../types';
import DeleteAlertModal from '../../../../../../../shared/components/delete-alert-modal';
import ProspectFiltersSaveAsAViewModal from '../../../../prospect-filters-modal/components/prospect-filters-save-as-a-view-modal';
import type { IProps } from './saved-filter-chip/saved-filter-chip-container';

const SavedFilterChip: React.FC<IProps> = ({
  selectedSavedViewId,
  filterId,
  isCountLoading,
  onFilterApplied,
  onRemoveFilter,
  prospectCount,
  label = '',
  filter,
  selectedSavedView,

  // Redux props
  teamsList,
  getTeamsListRequestStatus,
  sendGetTeamsListRequest,
  pinUnpinSavedFilterRequestStatus,
  updateSavedFilterRequestStatus,
  deleteSavedViewRequestStatus,
  sendPinUnpinSavedFilterRequest,
  sendUpdateSavedFilterRequest,
  sendDeleteSavedViewRequest,
  resetPinUnpinSavedFilterRequest,
  handlePinUnpin,
}) => {
  const history = useHistory();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [currentView, setCurrentView] = useState<{
    viewId: number;
    name: string;
    isShared: boolean;
    teams: any[];
  }>(null);

  const isSelected = selectedSavedViewId === filterId;
  const isPinned = !isEmpty(filter?.pinnedAt);

  const chipClassNames = classNames([
    getSavedViewDefaultClassNames(),
    {
      'saved-filters__item-active': isSelected,
    },
  ]);

  const showEditModal = () => {
    setCurrentView({
      viewId: filter.id,
      name: filter.filterName,
      isShared: filter.isShared,
      teams: filter.teams || [],
    });
    setIsEditModalVisible(true);
  };

  const showDeleteModal = () => {
    setCurrentView({
      viewId: filter.id,
      name: filter.filterName,
      isShared: filter.isShared,
      teams: [],
    });
    setIsDeleteModalVisible(true);
  };

  const handlePinUnpinView = () => {
    handlePinUnpin(
      filter.id,
      isEmpty(filter.pinnedAt)
        ? PinUnpinActionType.Pin
        : PinUnpinActionType.UnPin,
    );
  };

  const handleUpdateView = useCallback(
    (filterName: string, isShared: boolean, teamIds: number[]) => {
      if (currentView?.viewId) {
        sendUpdateSavedFilterRequest(currentView.viewId, {
          filterName,
          isShared,
          teamIds,
        });
      }
    },
    [currentView?.viewId, sendUpdateSavedFilterRequest],
  );

  const handleDeleteSavedView = useCallback(() => {
    sendDeleteSavedViewRequest(currentView.viewId);
  }, [currentView?.viewId, sendDeleteSavedViewRequest]);

  const hideEditModal = () => {
    setIsEditModalVisible(false);
    setCurrentView(null);
  };

  // Handle Edit success
  useEffect(() => {
    executeOnRequestStatus({
      status: updateSavedFilterRequestStatus,
      onSuccess: () => {
        setIsEditModalVisible(false);
        setCurrentView(null);
      },
    });
  }, [updateSavedFilterRequestStatus]);

  // Handle Delete success
  useEffect(() => {
    executeOnRequestStatus({
      status: deleteSavedViewRequestStatus,
      onSuccess: () => {
        if (
          currentView?.viewId &&
          selectedSavedView?.id === currentView.viewId
        ) {
          // If deleting the selected view, clear URL which will clear activeViewId
          onRemoveFilter(true);
        }
      },
    });
  }, [
    deleteSavedViewRequestStatus,
    currentView,
    selectedSavedView,
    onRemoveFilter,
  ]);

  const popoverContent = (
    <div className="saved-filter-chip-popover">
      <div
        className="saved-filter-chip-popover-item d-flex align-items-center gap-8"
        {...accessibleOnClick(() => {
          showEditModal();
          setIsPopoverOpen(false);
        })}
      >
        <Pencil width={16} height={16} className="gray-txt-15" />
        <span className="regular-2 blue-txt-16">Edit</span>
      </div>
      <div
        className="saved-filter-chip-popover-item d-flex align-items-center gap-8"
        {...accessibleOnClick(() => {
          handlePinUnpinView();
          setIsPopoverOpen(false);
        })}
      >
        {isPinned ? (
          <div className="pin-view-icon">
            <Pined width={16} height={16} />
          </div>
        ) : (
          <div className="pin-view-icon">
            <Pin width={16} height={16} className="gray-txt-15" />
          </div>
        )}
        <span className="regular-2 blue-txt-16">
          {isPinned ? 'Unpin' : 'Pin'}
        </span>
      </div>
      <div
        className="saved-filter-chip-popover-item d-flex align-items-center gap-8"
        {...accessibleOnClick(() => {
          showDeleteModal();
          setIsPopoverOpen(false);
        })}
      >
        <Trash width={16} height={16} className="gray-txt-15" />
        <span className="regular-2 blue-txt-16">Delete</span>
      </div>
    </div>
  );

  const chipContent = (
    <>
      <TruncatedTextWithTooltip length={isSelected ? 25 : 20} string={label}>
        {isSelected ? truncate(label, 25, false) : truncate(label, 20, false)}
      </TruncatedTextWithTooltip>

      {selectedSavedViewId && isSelected && (
        <div className="d-flex align-items-center gap-4 ml-1">
          {!isCountLoading && (
            <div className="saved-filters__item-count regular-1 font-medium line-height-16 blue-txt-17">
              {getFormatNumber(prospectCount)}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (isSelected) {
    return (
      <>
        <OverlayPopover
          placement={Placement.BottomStart}
          trigger={TriggerType.Click}
          content={popoverContent}
          show={isPopoverOpen}
          onToggle={(nextShow) => setIsPopoverOpen(nextShow)}
          rootClose
          className="saved-filters-chip-popover"
        >
          <div
            className={chipClassNames}
            {...accessibleOnClick((e) => {
              e?.stopPropagation?.();
              e?.preventDefault?.();
              // Let OverlayTrigger's click handler call onToggle; we only stop tab bar from receiving the click
              if (e?.type === 'keydown') {
                setIsPopoverOpen((prev) => !prev);
              }
            })}
          >
            {chipContent}
          </div>
        </OverlayPopover>

        {isEditModalVisible && currentView && (
          <ProspectFiltersSaveAsAViewModal
            show={isEditModalVisible}
            viewDetails={currentView}
            onHide={hideEditModal}
            onSave={handleUpdateView}
            teamsList={teamsList}
            getTeamsListRequestStatus={getTeamsListRequestStatus}
            sendGetTeamsListRequest={sendGetTeamsListRequest}
            isLoading={getIsRequestPending(updateSavedFilterRequestStatus)}
          />
        )}

        {isDeleteModalVisible && currentView && (
          <DeleteAlertModal
            show={isDeleteModalVisible}
            className="delete-saved-view"
            onClose={() => {
              setIsDeleteModalVisible(false);
              setCurrentView(null);
            }}
            onSubmit={handleDeleteSavedView}
            title="Delete Saved View"
            contents={[
              <span key="content">
                Are you sure you want to delete "
                <span className="break-word">{currentView?.name}</span>" saved
                view?
              </span>,
            ]}
            isSubmitLoading={getIsRequestPending(deleteSavedViewRequestStatus)}
            submitButtonText="Yes"
          />
        )}
      </>
    );
  }

  return (
    <div
      className={chipClassNames}
      {...accessibleOnClick(() => {
        if (!isSelected) {
          onFilterApplied();
        }
      })}
    >
      {chipContent}
    </div>
  );
};

export default SavedFilterChip;
